import type { IntlShape } from '@formatjs/intl'

import { assert, createMixin, EventListener } from '../../utils'

import {
    type AppState,
    ConnectwareError,
    ConnectwareErrorType,
    selectAuthenticatedCapabilitiesWithDefault,
    selectAuthenticatedToken,
    selectAuthenticatedUsername,
    selectMfaRequiredAuthenticationToken,
} from '../../domain'

import type {
    ApplicationStateService,
    AuthenticationPersistenceService,
    AuthenticationService,
    BrowserService,
    CertificatesService,
    ClientRegistryService,
    ColumnViewPersistenceService,
    ConfigurationService,
    ConnectwareLogsService,
    ConnectwareResourcesManagementService,
    ConnectwareServicesService,
    FileService,
    LoggerService,
    MfaService,
    RecordPanelWidthPersistenceService,
    RuleEngineService,
    SubscriptionsService,
    SystemService,
    TablePersistenceService,
    TopicsPersistenceService,
    TopicsService,
    TranslationService,
    UsecaseServices,
    UserService,
} from '../../application'

import type { ConnectwareAdminWebAppConfig } from '../config'
import { createRSTAdapter, pathsValues } from '../Connectware'
import { CybusConfigurationService } from '../CybusConfiguration'
import { WebFileService } from '../FileService'
import { createTranslationInternationalization, FormatJSTranslationService } from '../FormatJS'
import { HTML5BrowserService } from '../HTML5'
import {
    ConnectwareHTTPAuthenticationService,
    ConnectwareHTTPCertificatesService,
    ConnectwareHTTPClientRegistryService,
    ConnectwareHTTPLogsService,
    ConnectwareHTTPMfaService,
    ConnectwareHTTPResourcesManagementService,
    ConnectwareHTTPRuleEngineService,
    type ConnectwareHTTPServiceOptions,
    ConnectwareHTTPServicesService,
    ConnectwareHTTPSubscriptionsService,
    ConnectwareHTTPTopicsService,
    ConnectwareHTTPUserService,
    CybusSystemService,
    type HttpResponse,
    isMissingPermissionsResponse,
    RSTDeviationHTTPService,
    RSTStatusTypeHTTPService,
} from '../http'
import { type MqttMessageFromWorker, type MqttMessageToWorker, MqttTopicsService } from '../mqtt'
import { type RSTBaseSubscriptionOptions, RSTDeviationsSubscription, RSTStatusTypeSubscription } from '../RSTWebSocket'
import { ConnectwareRuleEngineService } from '../RuleEngine'
import {
    WebStorageAuthenticationPersistenceService,
    WebStorageColumnViewPersistenceService,
    WebStorageRecordPanelWidthPersistenceService,
    WebStorageTablePersistenceService,
    WebStorageTopicsPersistenceService,
} from '../webstorage'
import { WrappedWorker } from '../workers'
import { getMqttWorkerRef } from './workers'
import type { TranslationProviderProps } from '../react/Internationalization'
import { ExtendedStateManager } from '../react/utils'

/**
 * This class is here to enable the PermissionSplitter to do its job
 * But it also collects the many technologies used in the Admin UI
 * to be injected into the application's usecases
 */
class InjectedServices implements UsecaseServices {
    readonly subscriptionsService: SubscriptionsService

    readonly connectwareResourcesManagementService: ConnectwareResourcesManagementService

    readonly connectwareLogsService: ConnectwareLogsService

    readonly connectwareServicesService: ConnectwareServicesService

    readonly authenticationService: AuthenticationService

    readonly browserService: BrowserService

    readonly authenticationPersistenceService: AuthenticationPersistenceService

    readonly userService: UserService

    readonly clientRegistryService: ClientRegistryService

    readonly configurationService: ConfigurationService

    readonly systemService: SystemService

    readonly fileService: FileService

    readonly translationService: TranslationService

    readonly topicsService: TopicsService

    readonly ruleEngineService: RuleEngineService

    readonly topicsPersistenceService: TopicsPersistenceService

    readonly certificatesService: CertificatesService

    readonly tablePersistenceService: TablePersistenceService

    readonly columnViewPersistenceService: ColumnViewPersistenceService

    readonly recordPanelWidthPersistenceService: RecordPanelWidthPersistenceService

    readonly mfaService: MfaService

    constructor (
        readonly applicationState: ApplicationStateService,
        readonly logger: LoggerService,
        intl: IntlShape<string>,
        config: ConnectwareAdminWebAppConfig,
        httpResponseListener: EventListener<HttpResponse>
    ) {
        const { displayVersion, backendHost, workbenchPath, services, userManagement, rst, mqtt, enableStorybook } = config

        /**
         * Either use the custom backend host, or fall back to the current url
         */
        const host = backendHost ?? location.host
        const httpsBackendURL = new URL(`https://${host}`)
        const wssBackendURL = new URL(`wss://${host}`)

        const createStateSelector = <T, A extends unknown[]>(selector: (s: AppState, ...args: A) => T): (...args: A) => T => (...args) =>
            selector(this.applicationState.getState(), ...args)

        const selectCapabilities = createStateSelector(selectAuthenticatedCapabilitiesWithDefault)
        const getToken = createStateSelector(selectAuthenticatedToken)
        const tokenOrMfaTokenSelector = createStateSelector((s) => selectMfaRequiredAuthenticationToken(s) ?? selectAuthenticatedToken(s))
        const baseMqttAuthenticationSelector = createStateSelector((state, path: string) => {
            const username = selectAuthenticatedUsername(state)
            const password = selectAuthenticatedToken(state)
            assert(password !== null && username !== null, new ConnectwareError(ConnectwareErrorType.AUTHENTICATION, 'Authentication is missing'))
            return { url: new URL(path, wssBackendURL), username, password }
        })

        this.translationService = new FormatJSTranslationService(intl)
        const ruleEngineServiceTransform = new ConnectwareRuleEngineService()

        /**
         * Web services
         */
        this.browserService = new HTML5BrowserService(
            logger,
            httpsBackendURL.origin,
            new Set([httpsBackendURL.host, location.host]),
            new Set([services.externalUrlBasePath, workbenchPath, ...pathsValues['resource-status-tracking']])
        )
        this.fileService = new WebFileService()
        this.authenticationPersistenceService = new WebStorageAuthenticationPersistenceService(window.localStorage, displayVersion)
        this.topicsPersistenceService = new WebStorageTopicsPersistenceService(window.localStorage, displayVersion)
        this.tablePersistenceService = new WebStorageTablePersistenceService(window.localStorage, displayVersion)
        this.columnViewPersistenceService = new WebStorageColumnViewPersistenceService(window.localStorage, displayVersion)
        this.recordPanelWidthPersistenceService = new WebStorageRecordPanelWidthPersistenceService(window.localStorage, displayVersion)

        /**
         * Due to how changes are expected on the domain and business layers, propagating resource changes SHOULD ONLY
         * be done under the hood and without extra work from the rendering layer
         *
         * This listener hooks "the http resource service" to "the http subscription service" and
         * ensures that whenever a service changes, there will be an immediate change on the UI
         */
        const resourceChangeListeners = new EventListener<void>()

        /**
         * Http services
         */
        const httpOptions: ConnectwareHTTPServiceOptions = { baseURL: httpsBackendURL.toString(), getToken, responseHandler: httpResponseListener }
        this.userService = new ConnectwareHTTPUserService(
            httpOptions,
            {
                usersConfig: { protectedRoles: new Set(userManagement.protectedRoles), protectedUsers: new Set(userManagement.protectedUsers) },
                pagination: { pageSize: userManagement.pageSize, searchSize: userManagement.searchSize },
                cacheInterval: userManagement.indexesCache,
            },
            this.translationService
        )
        this.clientRegistryService = new ConnectwareHTTPClientRegistryService(httpOptions, this.translationService)
        this.authenticationService = new ConnectwareHTTPAuthenticationService(httpOptions)
        this.configurationService = new CybusConfigurationService({
            httpsURL: httpsBackendURL,
            wssURL: wssBackendURL,
            salesEmail: config.contact.salesEmail,
            supportEmail: config.contact.supportEmail,
            mqttPaths: [mqtt.path],
            workbenchPath,
            version: displayVersion,
            pageSizes: [config.pageSizes.short, config.pageSizes.default, config.pageSizes.large, config.pageSizes.max],
            clientRegistryRefreshRate: userManagement.clientRegistryRefreshRate,
            certificateFileExtension: userManagement.certificateFileExtension,
            infoPageRefreshRate: config.infoPageRefreshRate,
            licenseFileExtension: config.licenseFileExtension,
            commissioningFile: { uploadExtensions: services.commissioningFile.extensions, downloadMimeType: services.commissioningFile.downloadMimeType },
            interactionThrottle: config.interactionThrottle,
            documentation: new URL(config.documentation),
            searchMinimum: config.searchMinimum,
            logoutWarningPeriod: config.logoutWarningPeriod,
            enableStorybook,
        })
        this.systemService = new CybusSystemService(httpOptions, displayVersion)
        this.certificatesService = new ConnectwareHTTPCertificatesService(httpOptions)

        const websocketOptions: RSTBaseSubscriptionOptions = {
            origin: wssBackendURL.origin,
            cacheLifespan: rst.cacheLifespan,
            reconnectTimeout: rst.reconnectTimeout,
            pageSize: rst.maxPageSize,
        }
        const rstAdapter = createRSTAdapter(
            new RSTStatusTypeHTTPService(httpOptions, rst.maxPageSize),
            new RSTDeviationHTTPService(httpOptions, rst.maxPageSize),
            new RSTStatusTypeSubscription(websocketOptions),
            new RSTDeviationsSubscription(websocketOptions)
        )

        this.subscriptionsService = new ConnectwareHTTPSubscriptionsService({
            ...httpOptions,
            getCapabilities: selectCapabilities,
            translationService: this.translationService,
            resourceChangeListeners,
            rstAdapter,
            updateThrottle: config.resourcesRefreshThrottle,
            pollingInterval: config.resourcesRefreshRate,
        })

        this.connectwareResourcesManagementService = new ConnectwareHTTPResourcesManagementService(
            httpOptions,
            this.translationService,
            resourceChangeListeners
        )
        this.connectwareLogsService = new ConnectwareHTTPLogsService(httpOptions, this.translationService)
        this.connectwareServicesService = new ConnectwareHTTPServicesService(httpOptions, resourceChangeListeners)
        this.mfaService = new ConnectwareHTTPMfaService({ ...httpOptions, getToken: tokenOrMfaTokenSelector }, this.translationService)
        const httpRuleEngineService = new ConnectwareHTTPRuleEngineService(httpOptions, this.translationService, {
            searchMinimum: config.searchMinimum,
            maxPageSize: config.pageSizes.max,
        })
        const httpTopicsService = new ConnectwareHTTPTopicsService(httpOptions, this.translationService)

        /**
         * Mqtt services
         */
        const mqttTopicsService = new MqttTopicsService(
            () => ({
                ...baseMqttAuthenticationSelector(mqtt.path),
                worker: new WrappedWorker<MqttMessageToWorker, MqttMessageFromWorker>(getMqttWorkerRef()),
            }),
            { cycle: mqtt.cycle, maxTopicDepth: mqtt.maxTopicDepth, timeout: mqtt.timeout, translationService: this.translationService }
        )

        this.topicsService = createMixin<TopicsService>({
            createSubscription: mqttTopicsService,
            createTopic: mqttTopicsService,
            fetchTopicsChunk: httpTopicsService,
        })
        this.ruleEngineService = createMixin<RuleEngineService>({ transform: ruleEngineServiceTransform, fetchEndpoints: httpRuleEngineService })
    }
}

type ParcelInjections = Readonly<{
    services: UsecaseServices

    state: ExtendedStateManager<AppState>
    internationalization: TranslationProviderProps['internationalization']

    missingPermissionsListener: Pick<EventListener<HttpResponse>, 'on'>
}>

export const resolveInjections = (appState: AppState, config: ConnectwareAdminWebAppConfig): ParcelInjections => {
    const state = new ExtendedStateManager<AppState>(appState)
    const logger: LoggerService = console
    const internationalization = createTranslationInternationalization(config.translations, logger)
    const missingPermissionsListener = new EventListener<HttpResponse>(isMissingPermissionsResponse)

    return {
        services: new InjectedServices(state, logger, internationalization, config, missingPermissionsListener),
        state,
        internationalization,
        missingPermissionsListener,
    }
}
